import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Plus, MessageSquare, LayoutGrid, Trash2, ArrowLeft, LogOut, Eye, Users, TrendingUp, Dna, BrainCircuit, Radar, Waypoints, Settings } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { SecuritySettings } from "@/components/SecuritySettings";

export default function Sidebar({ conversations, activeId, onSelect, onNew, onDelete, view, onViewChange, mobileVariant = false }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [securityOpen, setSecurityOpen] = useState(false);
  const [build, setBuild] = useState(null);

  useEffect(() => {
    api.get("/version").then((r) => {
      const d = r.data || {};
      const when = d.boot ? new Date(d.boot).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "";
      setBuild(d.sha ? `${d.sha} · ${when}` : when);
    }).catch(() => {});
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  // When mounted inside the mobile drawer the sidebar takes the drawer's full width;
  // in the desktop grid it still holds its 2/12 column span.
  const shellClasses = mobileVariant
    ? "h-full flex flex-col glass-panel rounded-2xl p-4 overflow-hidden"
    : "md:col-span-2 h-full flex flex-col glass-panel rounded-2xl p-4 overflow-hidden";

  return (
    <div className={shellClasses} data-testid="os-sidebar">
      <button
        data-testid="sidebar-return-mothership"
        onClick={() => navigate("/")}
        className="flex items-center gap-2 text-slate-500 hover:text-cyan-400 transition-colors text-[10px] font-mono-data uppercase tracking-widest mb-5"
      >
        <ArrowLeft className="w-3 h-3" /> Mothership
      </button>

      <div className="flex gap-1.5 mb-5 flex-wrap">
        <button
          data-testid="nav-brain-console"
          onClick={() => onViewChange("console")}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-[10px] font-mono-data uppercase tracking-wider transition-colors ${view === "console" ? "bg-cyan-400/15 text-cyan-300 border border-cyan-400/30" : "text-slate-500 hover:text-slate-300 border border-transparent"}`}
        >
          <MessageSquare className="w-3.5 h-3.5" /> Brain
        </button>
        <button
          data-testid="nav-docking-bay"
          onClick={() => onViewChange("docking")}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-[10px] font-mono-data uppercase tracking-wider transition-colors ${view === "docking" ? "bg-amber-400/15 text-amber-300 border border-amber-400/30" : "text-slate-500 hover:text-slate-300 border border-transparent"}`}
        >
          <LayoutGrid className="w-3.5 h-3.5" /> Dock
        </button>
        <button
          data-testid="nav-vision"
          onClick={() => onViewChange("vision")}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-[10px] font-mono-data uppercase tracking-wider transition-colors ${view === "vision" ? "bg-purple-400/15 text-purple-300 border border-purple-400/30" : "text-slate-500 hover:text-slate-300 border border-transparent"}`}
        >
          <Eye className="w-3.5 h-3.5" /> Vision
        </button>
        <button
          data-testid="nav-people"
          onClick={() => onViewChange("people")}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-[10px] font-mono-data uppercase tracking-wider transition-colors ${view === "people" ? "bg-emerald-400/15 text-emerald-300 border border-emerald-400/30" : "text-slate-500 hover:text-slate-300 border border-transparent"}`}
        >
          <Users className="w-3.5 h-3.5" /> People
        </button>
        <button
          data-testid="nav-gaps"
          onClick={() => onViewChange("gaps")}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-[10px] font-mono-data uppercase tracking-wider transition-colors ${view === "gaps" ? "bg-rose-400/15 text-rose-300 border border-rose-400/30" : "text-slate-500 hover:text-slate-300 border border-transparent"}`}
        >
          <TrendingUp className="w-3.5 h-3.5" /> Gaps
        </button>
        <button
          data-testid="nav-core"
          onClick={() => onViewChange("core")}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-[10px] font-mono-data uppercase tracking-wider transition-colors ${view === "core" ? "bg-cyan-400/15 text-cyan-300 border border-cyan-400/30" : "text-slate-500 hover:text-slate-300 border border-transparent"}`}
        >
          <Dna className="w-3.5 h-3.5" /> Core
        </button>
        <button
          data-testid="nav-neocortex"
          onClick={() => onViewChange("neocortex")}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-[10px] font-mono-data uppercase tracking-wider transition-colors ${view === "neocortex" ? "bg-cyan-400/15 text-cyan-200 border border-cyan-400/30" : "text-slate-500 hover:text-slate-300 border border-transparent"}`}
        >
          <Waypoints className="w-3.5 h-3.5" /> Neocortex
        </button>
        <button
          data-testid="nav-prefrontal"
          onClick={() => onViewChange("prefrontal")}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-[10px] font-mono-data uppercase tracking-wider transition-colors ${view === "prefrontal" ? "bg-cyan-400/15 text-cyan-300 border border-cyan-400/30" : "text-slate-500 hover:text-slate-300 border border-transparent"}`}
        >
          <BrainCircuit className="w-3.5 h-3.5" /> Cortex
        </button>
        <button
          data-testid="nav-radar"
          onClick={() => onViewChange("radar")}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-[10px] font-mono-data uppercase tracking-wider transition-colors ${view === "radar" ? "bg-emerald-400/15 text-emerald-300 border border-emerald-400/30" : "text-slate-500 hover:text-slate-300 border border-transparent"}`}
        >
          <Radar className="w-3.5 h-3.5" /> Radar
        </button>
      </div>

      <button
        data-testid="new-conversation-button"
        onClick={onNew}
        className="flex items-center justify-center gap-2 w-full py-2.5 mb-4 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 text-xs font-display font-medium transition-colors"
      >
        <Plus className="w-3.5 h-3.5" /> New Session
      </button>

      <p className="text-[9px] font-mono-data uppercase tracking-[0.2em] text-slate-600 mb-2 px-1">Session Log</p>
      <div className="flex-1 overflow-y-auto space-y-1 pr-1">
        {conversations.map((c) => (
          <motion.div
            key={c.id}
            data-testid={`conversation-item-${c.id}`}
            onClick={() => onSelect(c.id)}
            className={`group flex items-center justify-between gap-1 px-3 py-2.5 rounded-lg cursor-pointer text-xs transition-colors ${activeId === c.id ? "bg-cyan-400/10 text-cyan-200 border border-cyan-400/20" : "text-slate-400 hover:bg-white/5 border border-transparent"}`}
          >
            <span className="truncate flex-1">{c.title}</span>
            <button
              data-testid={`delete-conversation-${c.id}`}
              onClick={(e) => { e.stopPropagation(); onDelete(c.id); }}
              className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-red-400 transition-opacity"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </motion.div>
        ))}
        {conversations.length === 0 && (
          <p className="text-[11px] text-slate-600 px-1 py-4 text-center">No sessions yet. Start one.</p>
        )}
      </div>

      <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between">
        <span className="text-[10px] font-mono-data text-slate-600 truncate" data-testid="sidebar-current-user">{user?.email}</span>
        <div className="flex items-center gap-2.5">
          <button
            data-testid="security-settings-button"
            onClick={() => setSecurityOpen(true)}
            title="Security settings"
            className="text-slate-500 hover:text-cyan-300 transition-colors"
          >
            <Settings className="w-3.5 h-3.5" />
          </button>
          <button
            data-testid="logout-button"
            onClick={handleLogout}
            title="Log out"
            className="text-slate-500 hover:text-rose-400 transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      {build && (
        <p className="mt-1.5 text-[8px] font-mono-data text-slate-700 tracking-wider" data-testid="build-version" title="Deployed build">
          build · {build}
        </p>
      )}
      <SecuritySettings open={securityOpen} onClose={() => setSecurityOpen(false)} />
    </div>
  );
}
